import React, { useEffect, useRef } from 'react';
import { Checkbox } from 'react-bootstrap';
import Chart from './Chart.js';
import SubHeader from '../SubHeader.jsx';
import DatasetSelect from './DatasetSelect.jsx';
import XAxisSelect from './XAxisSelect.jsx';
import YAxisSelect from './YAxisSelect.jsx';
import {
  BAR_CHART_DATASET_TYPES,
  BAR_CHART_X_AXIS_TYPES,
} from './utils/index.js';
import {
  addYPercentageValue,
  formatChartData,
} from './utils/formatChartData.js';
import { getOrgTermsContent } from '../../content/utils.js';

const BarChart = ({
  reportOptions,
  onUpdateReportOptions,
  result,
  barChartError,
  query,
}) => {
  const chartRef = useRef();
  const legendRef = useRef();
  const isArchived =
    query.archived !== undefined &&
    (reportOptions.dataset === BAR_CHART_X_AXIS_TYPES[7].value ||
      reportOptions.group === BAR_CHART_DATASET_TYPES[7].value);

  const createChart = () => {
    const { percentage } = reportOptions;
    const width = document.body.clientWidth;
    const initialData = percentage ? addYPercentageValue(result) : result;
    const formattedData = formatChartData(initialData, percentage);

    Chart(
      chartRef,
      legendRef,
      initialData,
      formattedData,
      width,
      reportOptions
    );
  };

  useEffect(() => {
    createChart();
  }, [result, reportOptions.hideEmpty, reportOptions.percentage]);

  window.addEventListener('resize', () => createChart());
  return (
    <section className="bar-chart">
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Grouped Bar Chart</h2>
        </div>
        <div className="flex">
          <div className="m-r-18">
            <Checkbox
              className="label-left"
              checked={reportOptions.hideEmpty}
              onChange={() =>
                onUpdateReportOptions({ hideEmpty: !reportOptions.hideEmpty })
              }
            >
              <span className="text-muted" style={{ fontSize: '14px' }}>
                Hide Empty
              </span>
              <span className="checkmark"></span>
            </Checkbox>
          </div>
          <div className="m-r-18">
            <YAxisSelect
              selectedY={reportOptions.percentage}
              onSelect={onUpdateReportOptions}
            />
          </div>
          <div className="m-r-18">
            <XAxisSelect
              selectedX={reportOptions.group}
              onSelect={onUpdateReportOptions}
            />
          </div>
          <div className="m-r-18">
            <DatasetSelect
              selectedX={reportOptions.group}
              selectedDataset={reportOptions.dataset}
              onSelect={onUpdateReportOptions}
            />
          </div>
        </div>
      </SubHeader>
      <div className="flex flex-col" style={{ background: '#fff' }}>
        {isArchived ? (
          <div className="flex flex-col flex-align-center component component-xl w-100">
            <h2 className="m-b-12" style={{ color: '#27B0E0' }}>
              Report is not complete
            </h2>
            <p className="text-muted">
              To view the complete data in the report, please adjust the filter
              to show all {getOrgTermsContent('projects')} types: archived and
              active.
            </p>
          </div>
        ) : null}
        {barChartError ? (
          <div className="flex flex-col flex-align-center component component-xl w-100">
            <h2 className="m-b-12" style={{ color: '#27B0E0' }}>
              Dataset is too big
            </h2>
            <p className="text-muted">
              The Bar Chart cannot display all items due to the large number.
              Please use filters to adjust the results.
            </p>
          </div>
        ) : null}
        <div ref={chartRef} className="barchart-wrapper" />
        <div className="flex flex-center m-b-22 w-100">
          <h4>
            {
              BAR_CHART_X_AXIS_TYPES.find(
                (t) => t.value === reportOptions.group
              ).name
            }
          </h4>
        </div>
        <div className="component component-sm flex flex-align-start w-100">
          <h4 className="m-r-16 text-nowrap">
            {
              BAR_CHART_DATASET_TYPES.find(
                (t) => t.value === reportOptions.dataset
              ).name
            }
            :
          </h4>
          <div ref={legendRef} className="barchart-legend" />
        </div>
      </div>
    </section>
  );
};

export default BarChart;
